import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Bell, CheckCheck, AlertTriangle, ThermometerSun, Info, CheckCircle2, Trash2 } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const TYPE_CONFIG = {
  info:    { Icon: Info,           color: 'text-blue-500',      bg: 'bg-blue-100 dark:bg-blue-900/30'      },
  warning: { Icon: AlertTriangle,  color: 'text-red-500',       bg: 'bg-red-100 dark:bg-red-900/30'        },
  success: { Icon: CheckCircle2,   color: 'text-emerald-500',   bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  alert:   { Icon: ThermometerSun, color: 'text-amber-500',     bg: 'bg-amber-100 dark:bg-amber-900/30'    },
};

function formatTime(createdAt, t) {
  if (!createdAt) return '';
  const diff = Math.floor((Date.now() - new Date(createdAt)) / 1000);
  if (diff < 60)     return t('notificationDrawer.time.justNow');
  if (diff < 3600)   return t('notificationDrawer.time.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400)  return t('notificationDrawer.time.hoursAgo', { count: Math.floor(diff / 3600) });
  if (diff < 604800) return t('notificationDrawer.time.daysAgo', { count: Math.floor(diff / 86400) });
  return new Date(createdAt).toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

export default function NotificationDrawer({ open, onClose }) {
  const { t } = useTranslation();
  const { state, actions } = useApp();
  const navigate = useNavigate();
  const notifications = state.notifications ?? [];
  const unreadCount   = notifications.filter((n) => !n.read).length;

  // Trap scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleMarkRead = (n) => {
    if (!n.read) actions.markNotificationRead(n.id);
  };

  // Notifications that carry an in-app link (e.g. milk written off) open that page when tapped.
  const handleOpen = (n) => {
    handleMarkRead(n);
    if (n.link && n.link.startsWith('/')) {
      onClose();
      navigate(n.link);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer panel — slides from right */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[380px] flex flex-col bg-gray-50 dark:bg-gray-950 shadow-2xl transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* ── Header ──────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-primary-800 to-primary-600 px-5 pt-5 pb-6 flex-shrink-0 relative overflow-hidden">
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
          <div className="absolute top-6 right-10 w-12 h-12 bg-white/8 rounded-full" />

          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0 relative">
              <Bell size={22} className="text-white" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent-500 text-primary-900 text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-white font-bold text-xl leading-tight">{t('notificationDrawer.title')}</h2>
              <p className="text-primary-200 text-sm">
                {unreadCount > 0 ? t('notifications.unreadCount', { count: unreadCount }) : t('notificationDrawer.allCaughtUp')}
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl border border-white/30 flex items-center justify-center text-white hover:bg-white/20 transition-colors flex-shrink-0"
              aria-label={t('notificationDrawer.close')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Actions bar ─────────────────────────────────────── */}
        {unreadCount > 0 && (
          <div className="px-5 py-2.5 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('notificationDrawer.total', { count: notifications.length })}
            </span>
            <button
              onClick={() => actions.markAllNotificationsRead()}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary-700 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
            >
              <CheckCheck size={14} />
              {t('notificationDrawer.markAllRead')}
            </button>
          </div>
        )}

        {/* ── Notification list ────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                <Bell size={28} className="text-gray-400 dark:text-gray-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-700 dark:text-gray-300">{t('notificationDrawer.noNotifications')}</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-0.5">{t('notificationDrawer.allCaughtUp')}</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {notifications.map((n) => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
                return (
                  <div
                    key={n.id}
                    className={`w-full text-left px-5 py-4 flex gap-3 items-start ${
                      n.read ? 'bg-gray-50 dark:bg-gray-950' : 'bg-white dark:bg-gray-900'
                    }`}
                  >
                    <button
                      onClick={() => handleMarkRead(n)}
                      className={`${cfg.bg} w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}
                    >
                      <cfg.Icon size={18} className={cfg.color} />
                    </button>

                    <button onClick={() => handleOpen(n)} className="flex-1 min-w-0 text-left">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold leading-snug ${
                          n.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {n.title}
                        </p>
                        {!n.read && (
                          <div className="w-2 h-2 rounded-full bg-primary-600 flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed line-clamp-2">
                        {n.message}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">{formatTime(n.createdAt, t)}</p>
                    </button>

                    <button
                      onClick={() => actions.removeNotification(n.id)}
                      className="text-gray-300 dark:text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                      title={t('notificationDrawer.dismiss')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
